import { type CSSProperties, type ReactNode, type TouchEvent, useRef, useState } from 'react';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/**
 * **FI-53 (Sprint F 2026-05-21)** — Drawer mobile per il contenuto
 * dell'`<Aside>` (StepRail builder / LevelsRail Hub / StepRail level-up).
 *
 * Visibile solo su mobile (<900px): il trigger sostituisce il brand DW in
 * `<AppTopbar>` con un'icona menu (`lucide-react ≡`). Tap → apre uno Sheet
 * (Radix Dialog skinned DWR) da sinistra con il contenuto dell'aside.
 *
 * **Iterazione 2 (feedback Francesco 2026-05-21 post-PR review)**:
 *  - **Animazione slide in/out**: transition CSS native su `transform` con
 *    `data-[state=open]:translate-x-0` / `data-[state=closed]:-translate-x-full`.
 *    Niente plugin `tw-animate-css` (non installato): le classi
 *    `animate-in`/`slide-in-from-left` di shadcn di default erano inerti.
 *  - **Persistenza al click interno (default)**: `closeOnInsideTap` ora
 *    `false` di default. Click dentro NON chiude il drawer — la pagina
 *    sottostante si aggiorna (es. tap step → naviga + drawer resta aperto).
 *    Il caller può opt-in (`true`) se vuole il vecchio comportamento.
 *  - **Swipe-to-close**: drag verso sinistra > 80px chiude il drawer.
 *    Durante il drag il pannello segue il dito (translateX live). Al rilascio
 *    sotto soglia, ritorna a 0 con transition.
 *
 * **Modi di chiusura disponibili**: (a) backdrop tap (default Radix);
 * (b) ESC keydown (default Radix); (c) swipe-left ≥ 80px. **Niente icona X**
 * — `showCloseButton={false}` nello SheetContent (feedback Francesco
 * 2026-05-21 screenshot: layout più pulito).
 */

export interface MobileAsideDrawerProps {
  /**
   * Contenuto del drawer (tipicamente `<StepRail>` o `<LevelsRail>`).
   * Il drawer NON aggiunge un wrapper di styling: i contenuti sono renderizzati
   * direttamente nello SheetContent — coerenti con `<Aside>` template.
   */
  children: ReactNode;
  /**
   * Titolo per accessibilità del drawer (richiesto da Radix Dialog ma
   * visivamente nascosto via `sr-only`). Es. "Navigazione step" / "Storico
   * livelli". Default: "Menu".
   */
  title?: string;
  /** Label personalizzata per il trigger (a11y). Default: "Apri menu". */
  triggerLabel?: string;
  /**
   * Se `true`, un click qualsiasi dentro lo Sheet chiude il drawer.
   * **Default false** (iterazione 2): il drawer persiste e l'utente può
   * navigare la pagina sottostante (es. tap step → naviga; drawer resta
   * aperto per vedere il contesto). Backdrop / ESC / X chiudono.
   */
  closeOnInsideTap?: boolean;
  /**
   * Override classe Tailwind del bottone trigger. Default: stile coerente
   * con BuilderBrand (h-9 w-9 ink/paper).
   */
  triggerClassName?: string;
  /**
   * URL dell'artwork classe da mostrare come watermark di sfondo del
   * pannello (coerente con `<Aside watermarkUrl={...}>` desktop).
   * Pattern `.aside-watermark` definito in `src/styles/index.css`
   * (opacity 0.08, background center 30% contain).
   */
  watermarkUrl?: string | undefined;
}

interface DrawerStyle extends CSSProperties {
  '--wm-image'?: string;
  '--wm-opacity'?: number;
}

// Soglia in pixel del drag-left oltre la quale il rilascio chiude il drawer.
// 80px ≈ ~27% della larghezza tipica del pannello (300px) — bilancia tra
// "swipe deciso" e "trigger accidentale". Coerente con pattern bottom-sheet
// Material/iOS (~25-30% dell'asse principale).
const SWIPE_CLOSE_DISTANCE_THRESHOLD_PX = 80;

// **FI-77 (2026-05-22)** — soglia velocità (px/ms) per chiudere su flick
// rapidi anche con distanza piccola. 0.4 px/ms = default di `vaul`
// (libreria React drawer moderna, Radix-style — usata anche da shadcn).
// Riferimenti: Material 3 ~0.35 px/ms, iOS-style ~0.7 px/ms. 0.4 è la
// fascia sensibile-ma-non-iper-reattiva.
const SWIPE_CLOSE_VELOCITY_THRESHOLD_PX_PER_MS = 0.4;

// **FI-77** — distanza minima richiesta perché la velocità conti. Evita che
// un tap accidentale o un piccolo jitter venga interpretato come flick.
const SWIPE_MIN_DISTANCE_FOR_VELOCITY_PX = 15;

/**
 * Composizione completa: trigger + drawer in un singolo componente. Il
 * caller monta solo `<MobileAsideDrawer>` nello slot brand del topbar — la
 * gestione open/close + drag + animazione è interna.
 */
export function MobileAsideDrawer({
  children,
  title = 'Menu',
  triggerLabel = 'Apri menu',
  closeOnInsideTap = false,
  triggerClassName,
  watermarkUrl,
}: MobileAsideDrawerProps) {
  const [open, setOpen] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const dragStartX = useRef<number | null>(null);
  const dragStartTime = useRef<number | null>(null);
  const isDragging = useRef(false);

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;
    const touch = e.touches[0];
    if (!touch) return;
    dragStartX.current = touch.clientX;
    dragStartTime.current = Date.now();
    isDragging.current = true;
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging.current || dragStartX.current === null) return;
    const touch = e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - dragStartX.current;
    // Solo drag verso sinistra (chiusura). Drag a destra ignorato (no-op
    // per evitare che il pannello "rimbalzi" oltre la posizione open).
    if (dx <= 0) setDragOffset(dx);
  };

  const handleTouchEnd = () => {
    // FI-77: decisione chiusura = distance threshold OR velocity threshold.
    // Distance preserva il comportamento "drag lento >80px chiude" originale.
    // Velocity (vaul-default 0.4 px/ms) aggiunge "flick rapido chiude
    // anche con piccola distanza". Min distance 15px per velocity evita
    // che un tap accidentale o jitter venga interpretato come flick.
    const startTime = dragStartTime.current;
    const elapsedMs = startTime !== null ? Date.now() - startTime : 0;
    const distance = Math.abs(dragOffset);
    // dragOffset è negativo (drag verso sinistra) → velocity negativa.
    // Convertiamo a velocità in modulo per il confronto.
    const velocityAbs = elapsedMs > 0 ? distance / elapsedMs : 0;
    const meetsDistance = distance >= SWIPE_CLOSE_DISTANCE_THRESHOLD_PX;
    const meetsVelocity =
      distance >= SWIPE_MIN_DISTANCE_FOR_VELOCITY_PX &&
      velocityAbs >= SWIPE_CLOSE_VELOCITY_THRESHOLD_PX_PER_MS;
    isDragging.current = false;
    dragStartX.current = null;
    dragStartTime.current = null;
    if (meetsDistance || meetsVelocity) {
      setOpen(false);
    }
    // Reset offset comunque: Radix re-anima a chiuso (-translate-x-full)
    // se sopra soglia, oppure ritorna a 0 (translate-x-0) sotto soglia.
    setDragOffset(0);
  };

  const handleInsideClick = closeOnInsideTap ? () => setOpen(false) : undefined;

  // Durante il drag attivo applichiamo transform inline + disabilitiamo la
  // transition per seguire il dito 1:1. Al rilascio (`dragOffset === 0`) il
  // transform inline scompare e la classe Tailwind animata riprende il controllo.
  // Watermark applicato come CSS variables per il pattern `.aside-watermark`
  // (coerente con `<Aside>` template desktop).
  const baseStyle: DrawerStyle | undefined = watermarkUrl
    ? { '--wm-image': `url("${watermarkUrl}")`, '--wm-opacity': 0.08 }
    : undefined;
  const dragStyle =
    dragOffset !== 0
      ? { transform: `translateX(${dragOffset}px)`, transition: 'none' as const }
      : undefined;
  const mergedStyle: DrawerStyle | undefined =
    baseStyle || dragStyle ? { ...baseStyle, ...dragStyle } : undefined;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          className={cn(
            'bg-ink text-paper-bright grid h-9 w-9 cursor-pointer place-items-center transition-opacity hover:opacity-80',
            triggerClassName,
          )}
        >
          <Menu aria-hidden className="h-5 w-5" strokeWidth={2.2} />
        </button>
      </SheetTrigger>
      <SheetContent
        side="left"
        showCloseButton={false}
        className={cn(
          'bg-paper-soft border-rule isolate w-[300px] max-w-[85vw] gap-0 overflow-hidden border-r-2 p-0',
          // Animazione slide in/out via keyframes `--animate-dwr-drawer-*`
          // definite in `src/styles/index.css` (FI-53 Sprint F iter. 2).
          // `forwards` su out mantiene il pannello fuori-schermo mentre Radix
          // attende `animationend` prima di unmountare il Sheet.
          'data-[state=open]:animate-dwr-drawer-in data-[state=closed]:animate-dwr-drawer-out',
        )}
        style={mergedStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <SheetTitle className="sr-only">{title}</SheetTitle>
        {watermarkUrl ? <div aria-hidden className="aside-watermark" /> : null}
        <div className="relative flex-1 overflow-y-auto" onClick={handleInsideClick}>
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
